import React from 'react';
import { cn } from '../../utils/cn';

export interface FloatingLabelInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  /**
   * Enables the floating label pattern that keeps the label visible while typing.
   * When disabled, the component behaves like a regular input with a placeholder.
   */
  floating?: boolean;
  containerClassName?: string;
  showAsterisk?: boolean;
  asteriskPosition?: 'left' | 'right';
  endAdornment?: React.ReactNode;
}

const baseInputClasses =
  'peer w-full px-3 pt-3 pb-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary';

const floatingLabelClasses =
  'pointer-events-none absolute text-[9px] font-medium text-muted-foreground bg-background px-1 -top-2 left-3 transition-all duration-150 peer-placeholder-shown:text-sm peer-placeholder-shown:text-muted-foreground peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2';

export const FloatingLabelInput = React.forwardRef<HTMLInputElement, FloatingLabelInputProps>(
  (
    {
      id,
      label,
      floating = true,
      containerClassName,
      className,
      placeholder,
      showAsterisk = false,
      asteriskPosition = 'right',
      endAdornment,
      ...rest
    },
    ref
  ) => {
    const shouldFloat = floating;
    const resolvedPlaceholder = shouldFloat ? ' ' : placeholder ?? label;

    return (
      <div className={cn('relative', containerClassName)}>
        <input
          ref={ref}
          id={id}
          placeholder={resolvedPlaceholder}
          className={cn(
            baseInputClasses,
            shouldFloat ? 'placeholder:text-transparent' : 'placeholder:text-muted-foreground',
            endAdornment ? 'pr-10' : null,
            className
          )}
          {...rest}
        />
        {shouldFloat && (
          <label htmlFor={id} className={floatingLabelClasses}>
            {showAsterisk && asteriskPosition === 'left' && (
              <span className="text-red-500 mr-1">*</span>
            )}
            {label}
            {showAsterisk && asteriskPosition === 'right' && (
              <span className="text-red-500 ml-1">*</span>
            )}
          </label>
        )}
        {endAdornment && (
          <div className="absolute inset-y-0 right-2 flex items-center">
            {endAdornment}
          </div>
        )}
      </div>
    );
  }
);

FloatingLabelInput.displayName = 'FloatingLabelInput';
